/**
 * @file Navbar.jsx
 * @description Clean white navbar with green accent, GFG-style. Provides primary application navigation and user actions.
 * @module components/layout/Navbar
 */
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import UserAvatar from "../UserAvatar";
import Button from "../ui/Button";
import { FcGoogle } from "react-icons/fc";
import {
  FiMenu,
  FiX,
  FiLogOut,
  FiHome,
  FiBookOpen,
  FiTarget,
} from "react-icons/fi";

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component Navbar
 * @description The main navigation bar used across the application.
 * 
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @param {Array} props.navLinks - Optional array of custom navigation links.
 * @param {boolean} props.showAuthActions - Whether to display authentication-related actions (login/logout/profile).
 * @returns {JSX.Element} The rendered Navbar view.
 */
const Navbar = ({ navigate, navLinks = [], showAuthActions = true }) => {
  const { isAuthenticated, user, logout, signInWithGoogle } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  /* ══════════════════════════════════════════════════════════════
   * EFFECTS & HANDLERS
   * ══════════════════════════════════════════════════════════════ */

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 5);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const handleNavClick = (path) => { navigate(path); closeMobileMenu(); };
  const handleLogout = () => { logout(); closeMobileMenu(); };

  const defaultLinks = navLinks.length > 0
    ? navLinks
    : isAuthenticated
      ? [
        { id: "home", label: "Home", action: () => handleNavClick("/") },
        { id: "learning", label: "Learning", action: () => handleNavClick("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => handleNavClick("/dashboard") },
        { id: "dsa", label: "DSA Problems", action: () => handleNavClick("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => handleNavClick("/system-design") }
      ]
      : [
        { id: "home", label: "Home", action: () => handleNavClick("/") },
        { id: "learning", label: "Learning", action: () => handleNavClick("/learning") },
        { id: "dsa", label: "DSA Problems", action: () => handleNavClick("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => handleNavClick("/system-design") },
      ];

  return (
    <>
      <nav className={`fixed top-0 inset-x-0 z-50 bg-emerald-900 transition-shadow duration-300`}>
        <div className="max-w-8xl mx-auto px-6 sm:px-12 lg:px-16">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo + Links */}
            <div className="flex items-center gap-8">
              <button onClick={() => handleNavClick("/")} className="focus:outline-none flex-shrink-0" aria-label="Go to homepage">
                <img src="/logo.png" alt="SimpleCode" className="h-10 w-auto" />
              </button>

              <div className="hidden lg:flex items-center gap-1">
                {defaultLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={link.action}
                    className="px-3 py-1.5 text-md text-gray-300 hover:text-white rounded transition-colors duration-150"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              {showAuthActions && isAuthenticated && (
                <>
                  <button
                    onClick={() => handleNavClick("/profile")}
                    className="flex items-center gap-2 text-md text-gray-300 hover:text-white transition-colors duration-150"
                  >
                    <UserAvatar avatarUrl={user?.avatar_url} size={26} />
                    <span className="hidden md:inline capitalize">{user?.username || "Profile"}</span>
                  </button>
                  <button onClick={handleLogout} className="flex items-center gap-1.5 text-md text-gray-300 hover:text-red-500 transition-colors duration-150">
                    <FiLogOut className="w-4 h-4" />
                    <span className="hidden md:inline">Sign Out</span>
                  </button>
                </>
              )}

              {showAuthActions && !isAuthenticated && (
                <div className="hidden lg:flex items-center gap-2">
                  <Button variant="login" size="login" onClick={signInWithGoogle}>
                    <FcGoogle className="w-4 h-4" />
                    Sign in with Google
                  </Button>
                </div>
              )}

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden pl-2 py-2 text-gray-300 hover:text-white transition-colors duration-150"
                aria-label="Toggle navigation"
              >
                {isMobileMenuOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        className={`fixed inset-0 z-[60] lg:hidden flex justify-end transition-all duration-300 ease-in-out ${isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300" 
          onClick={closeMobileMenu} 
        />
        
        {/* Drawer */}
        <div 
          className={`relative w-64 max-w-[80vw] bg-emerald-900 h-full shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-300">
            <span className="font-semibold text-gray-300">Menu</span>
            <button 
              onClick={closeMobileMenu} 
              className="p-1 -mr-1 text-gray-300 hover:text-white rounded-md transition-colors"
              aria-label="Close menu"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          
          {/* Links */}
          <div className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
            {!defaultLinks.some(link => link.id === 'home' || link.label === 'Home') && typeof window !== 'undefined' && window.location.pathname !== '/' && (
              <button 
                onClick={() => handleNavClick("/")} 
                className="block w-full text-left px-1 py-2 text-md text-gray-300 hover:text-white rounded transition-colors duration-150"
              >
                Home
              </button>
            )}
            
            {defaultLinks.map((link) => (
              <button 
                key={link.id}
                onClick={link.action}
                className="block w-full text-left px-1 py-2 text-md text-gray-300 hover:text-white rounded transition-colors duration-150"
              >
                {link.label}
              </button>
            ))}

            <div className="pt-6 mt-4 border-t border-emerald-800 space-y-2">
              {isAuthenticated ? (
                <>
                  <button 
                    onClick={() => handleNavClick("/profile")} 
                    className="flex items-center gap-2 w-full text-left px-1 py-2 text-md text-gray-300 hover:text-white rounded transition-colors duration-150"
                  >
                    <UserAvatar avatarUrl={user?.avatar_url} size={24} />
                    <span className="capitalize">{user?.username || "Profile"}</span>
                  </button>
                  <button 
                    onClick={handleLogout} 
                    className="flex items-center gap-2 w-full text-left px-1 py-2 text-md text-gray-300 hover:text-red-500 rounded transition-colors duration-150"
                  >
                    <FiLogOut className="w-4 h-4" /> Sign Out
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => { signInWithGoogle(); closeMobileMenu(); }} 
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 mt-2 text-sm font-medium text-gray-800 bg-white hover:bg-gray-100 rounded transition-colors duration-150 shadow-sm"  
                >
                  <FcGoogle className="w-5 h-5" /> Sign in with Google
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
